/**
 * Maps a `carrier/unassign` 200 response onto the notification the UI shows.
 *
 * The endpoint's `errors` array carries vendor text, internal `[CARRIER_UNASSIGN]` prefixes and
 * route ids, none of which is actionable to an operator -- the durable record of what actually
 * failed is the `integration_errors` rows the endpoint writes, plus Sentry. So the UI states the
 * outcome generically and tells the operator what to do about it.
 *
 * Split on `cleared` rather than on `errors.length` alone. A cancellation can fail at the vendor
 * with the assignment cleared on our side, or the compare-and-set at the end of the endpoint can
 * decline to clear at all because the order changed mid-flight. Those need opposite follow-up --
 * the second leaves the carrier still assigned -- and one message over both would be wrong for one
 * of them.
 *
 * Non-2xx responses never reach here: axios throws, and the callers' catch blocks own that path.
 *
 * @param {Object} resp - the axios response from `post(UNASSIGN_CARRIER, ...)`.
 * @returns {{severity: string, message: string}} severity and copy for the notification.
 */
export const unassignCarrierMessage = (resp) => {
    const errors = resp?.data?.errors;

    if (!errors?.length) {
        return { severity: 'success', message: 'Carrier unassigned successfully.' };
    }

    // Strictly `false`, so a response that reports errors without the flag is read as cleared --
    // which is what the endpoint does on every path that reaches its final update.
    if (resp.data.cleared === false) {
        return {
            severity: 'error',
            message: 'Carrier was not unassigned. Reach out to the carrier to confirm unassignment, then try again.',
        };
    }

    return {
        severity: 'warning',
        message: 'Carrier unassigned. Reach out to the carrier to confirm unassignment.',
    };
};
